import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Unplug } from "lucide-react";
import { Button } from "@/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import {
  FormCard,
  FormStackedRow,
  InlineMessage,
  SettingsHeader,
} from "@/components/settings/settings-ui";
import { formatRelativeTime } from "@/utils";
import { useDevicesQuery } from "@/data/devices";
import {
  googleConnectUrl,
  useDisconnectGoogleMutation,
  useGoogleIntegrationQuery,
  useGoogleRoomsQuery,
  useMapRoomMutation,
} from "@/data/integrations";
import type { GoogleDirectoryRoom } from "@/data/integrations";

interface IntegrationsSectionProps {
  workspaceSlug?: string | null;
  canManage: boolean;
}

const CALLBACK_ERROR_COPY: Record<string, string> = {
  access_denied: "Google connection was cancelled.",
  missing_scopes:
    "Google connection needs both requested permissions — reconnect and keep both checkboxes ticked.",
  no_refresh_token: "Google didn't issue offline access. Try connecting again.",
  not_configured: "Google integration isn't configured on this server.",
  exchange_failed: "Google connection failed. Try again.",
  missing_code: "Google connection failed. Try again.",
};

// The OAuth round-trip lands back on this settings tab with ?google=connected
// or ?google_error=…; surface it once as a toast, then clean the URL.
function consumeCallbackParams(): void {
  const params = new URLSearchParams(window.location.search);
  const connected = params.get("google");
  const error = params.get("google_error");
  if (!connected && !error) return;
  if (connected === "connected") {
    toast.success("Google Workspace connected");
  } else if (error) {
    toast.error(
      CALLBACK_ERROR_COPY[error] ?? "Google connection failed. Try again.",
    );
  }
  params.delete("google");
  params.delete("google_error");
  const search = params.toString();
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}`,
  );
}

export function IntegrationsSection({
  workspaceSlug,
  canManage,
}: IntegrationsSectionProps) {
  const statusQuery = useGoogleIntegrationQuery(workspaceSlug);
  const disconnectMutation = useDisconnectGoogleMutation(workspaceSlug ?? null);
  const [disconnectArmed, setDisconnectArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    consumeCallbackParams();
  }, []);

  const status = statusQuery.data;
  const connection = status?.connection ?? null;

  function connect() {
    if (!workspaceSlug) return;
    window.location.href = googleConnectUrl(workspaceSlug);
  }

  async function disconnect() {
    setBusy(true);
    setDisconnectArmed(false);
    try {
      await disconnectMutation.mutateAsync();
      toast.success("Google Workspace disconnected");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Integrations"
        description="Connect external services to this workspace."
      />

      <div className="space-y-6">
        <FormCard>
          <FormStackedRow
            label="Google Workspace"
            hint="Meetings booked in connected room calendars start and stop recordings on the room's Parable device."
          >
            {statusQuery.isPending && workspaceSlug ? (
              <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading connection
              </div>
            ) : statusQuery.isError ? (
              <InlineMessage variant="error">
                {statusQuery.error instanceof Error
                  ? statusQuery.error.message
                  : String(statusQuery.error)}
              </InlineMessage>
            ) : connection ? (
              <div className="space-y-3">
                <div className="grid items-center gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="truncate text-[13px] font-medium text-foreground">
                        Connected as {connection.google_email}
                      </div>
                      <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-300">
                        Connected
                      </span>
                    </div>
                    <div className="truncate text-[12px] text-muted-foreground">
                      connected {formatSettingsDate(connection.connected_at)} ·
                      last sync {formatLastSync(connection.last_sync_at)}
                    </div>
                  </div>
                  {canManage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant={disconnectArmed ? "destructive" : "outline"}
                      disabled={busy}
                      onClick={() =>
                        disconnectArmed
                          ? void disconnect()
                          : setDisconnectArmed(true)
                      }
                      onBlur={() =>
                        disconnectArmed && setDisconnectArmed(false)
                      }
                    >
                      {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Unplug className="h-3.5 w-3.5" />
                      )}
                      {disconnectArmed ? "Confirm disconnect" : "Disconnect"}
                    </Button>
                  ) : null}
                </div>
                {connection.last_sync_error ? (
                  <InlineMessage variant="error">
                    Sync error: {connection.last_sync_error}
                  </InlineMessage>
                ) : null}
              </div>
            ) : status && !status.configured ? (
              <InlineMessage variant="info">
                Google integration isn't configured on this server yet.
              </InlineMessage>
            ) : canManage ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-[13px] text-muted-foreground">
                  A Google Workspace admin connects once for the whole
                  workspace; room calendars are mapped to devices afterwards.
                </div>
                <Button type="button" size="sm" onClick={connect}>
                  <CalendarClock className="h-3.5 w-3.5" />
                  Connect Google Workspace
                </Button>
              </div>
            ) : (
              <InlineMessage variant="info">
                Only workspace admins can connect Google Workspace.
              </InlineMessage>
            )}
          </FormStackedRow>
        </FormCard>

        {connection ? (
          <RoomMappingCard
            workspaceSlug={workspaceSlug ?? null}
            canManage={canManage}
          />
        ) : null}
      </div>
    </div>
  );
}

// Sentinel for "no device" — radix Select can't represent an empty-string
// item value.
const UNMAPPED = "__unmapped__";

function RoomMappingCard({
  workspaceSlug,
  canManage,
}: {
  workspaceSlug: string | null;
  canManage: boolean;
}) {
  const roomsQuery = useGoogleRoomsQuery(workspaceSlug, true);
  const devicesQuery = useDevicesQuery(workspaceSlug);
  const mapRoom = useMapRoomMutation(workspaceSlug);
  const [pendingCalendarId, setPendingCalendarId] = useState<string | null>(
    null,
  );

  const rooms = roomsQuery.data ?? [];
  const devices = devicesQuery.data ?? [];

  async function setMapping(calendarId: string, deviceId: string | null) {
    setPendingCalendarId(calendarId);
    try {
      await mapRoom.mutateAsync({ calendarId, deviceId });
      toast.success(
        deviceId ? "Room mapped to device" : "Room mapping cleared",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setPendingCalendarId(null);
    }
  }

  return (
    <FormCard>
      <FormStackedRow
        label="Room calendars"
        hint="Map each room to the Parable device inside it. Mapped rooms sync their meetings every minute; auto-started recordings arrive in a later release."
      >
        {roomsQuery.isPending ? (
          <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading rooms from Google
          </div>
        ) : roomsQuery.isError ? (
          <InlineMessage variant="error">
            {roomsQuery.error instanceof Error
              ? roomsQuery.error.message
              : String(roomsQuery.error)}
          </InlineMessage>
        ) : rooms.length === 0 ? (
          <InlineMessage variant="info">
            No rooms found in your Google Workspace directory. Create a room
            resource in the Google Admin Console (Directory &rarr; Buildings and
            resources), then check back here.
          </InlineMessage>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
            {rooms.map((room) => (
              <li
                key={room.calendar_id}
                className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-[13px] font-medium text-foreground">
                      {room.resource_name}
                    </span>
                    {room.capacity != null ? (
                      <span className="shrink-0 text-[12px] text-muted-foreground">
                        seats {room.capacity}
                      </span>
                    ) : null}
                    {room.missing_from_directory ? (
                      <span className="shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300">
                        Removed from directory
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {roomSyncStatus(room)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {pendingCalendarId === room.calendar_id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : null}
                  {canManage && !devicesQuery.isPending ? (
                    <Select
                      value={room.device_id ?? UNMAPPED}
                      onValueChange={(value) =>
                        void setMapping(
                          room.calendar_id,
                          value === UNMAPPED ? null : value,
                        )
                      }
                      disabled={
                        pendingCalendarId != null ||
                        (room.missing_from_directory && room.device_id == null)
                      }
                    >
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder="No device" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNMAPPED}>No device</SelectItem>
                        {devices.map((device) => (
                          <SelectItem key={device.id} value={device.id}>
                            {device.name}
                          </SelectItem>
                        ))}
                        {/* A mapping can point at a device that no longer
                            lists (e.g. moved workspace); keep it selectable
                            so the value renders. */}
                        {room.device_id != null &&
                        !devices.some((d) => d.id === room.device_id) ? (
                          <SelectItem value={room.device_id}>
                            Unknown device
                          </SelectItem>
                        ) : null}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-[13px] text-muted-foreground">
                      {room.device_id == null
                        ? "No device"
                        : (devices.find((d) => d.id === room.device_id)?.name ??
                          "Unknown device")}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </FormStackedRow>
    </FormCard>
  );
}

function roomSyncStatus(room: GoogleDirectoryRoom): string {
  if (room.device_id == null) return "Not mapped — meetings are not synced";
  if (room.last_sync_error) return `Sync error: ${room.last_sync_error}`;
  if (room.last_synced_at == null) return "Mapped — waiting for first sync";
  const ms = new Date(room.last_synced_at).getTime();
  if (Number.isNaN(ms)) return "Mapped — waiting for first sync";
  return `Synced ${formatRelativeTime(ms)}`;
}

function formatSettingsDate(value: string | number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLastSync(value: string | number | null): string {
  if (value == null) return "never";
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return "never";
  return formatRelativeTime(ms);
}
